'use client';

import { API_ROUTES, useApiInvalidate } from '@shared/api-utils';
import { useSignIn } from '@shared/auth';
import { useDebug } from '@shared/debug';
import { ROUTES } from '@shared/utils';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useGlobalSheet } from '../../global-sheets/context/GlobalSheetContext';
import { useAltenarSDKLoaded } from '../AltenarFrame/AltenarSDKContext';
import { mapPageValue, withRacingType } from '../SportsbookNavigationMenu/utils';
import { useSportsbookRoutes } from '../SportsbookRoutesContext/SportsbookRoutesContext';
import { resolveSportsbookPathname } from '../utils/resolveSportsbookPathname';
import { buildSportsbookHref, buildUrlParamsFromRouteParams, updateUrlParams } from '../utils/sportsbookUrl';
import { centreAltenarPopups } from './centreAltenarPopup';
import type { RouteParams, SportsBookParams, SportsbookProps } from './types';

// Altenar WSDK reads --wsdk-internal-Overlay-offsetTop from this container (inherits into open shadow DOM).
const SPORTSBOOK_CONTAINER_CLASS =
  'min-h-screen overflow-hidden md:[--wsdk-internal-Overlay-offsetTop:calc(var(--sticky-primary-header-height,0px)+var(--sticky-page-toolbar-height,0px))]';

type WindowWithSportsbookSDK = Window & {
  altenarWSDK?: {
    addSportsBook: (config: { props: SportsBookParams; container: HTMLDivElement }) => Promise<{ remove: () => void }>;
  };
  omni?: {
    SB?: {
      remove: () => void;
    };
  };
};

/** Altenar `defaultStake` prop — CRM share-code links expect stake 10 in the betslip. */
const SHARE_CODE_DEFAULT_STAKE = 10;

const updatePageCssClasses = () => {
  const contentElement = document.querySelector('#content');
  const pageElement = document.querySelector('#page');

  if (contentElement) {
    contentElement.classList.add('sportsbook-page');
  }
  if (pageElement) {
    pageElement.classList.add('sportsbook-page');
  }
};

function SportsbookWidget({ quickDepositEnabled = true, page, className = '', routeParams }: SportsbookProps) {
  const sportsBookRef = useRef<HTMLDivElement>(null);
  const popupCentringContainerObserverRef = useRef<MutationObserver | null>(null);
  const popupCentringShadowObserverRef = useRef<MutationObserver | null>(null);
  const watchedShadowRootRef = useRef<ShadowRoot | null>(null);
  const routeChangeCountRef = useRef(0);
  const [isWidgetInitialized, setIsWidgetInitialized] = useState(false);
  const debug = useDebug();
  const { page: routePage = 'overview', ...initialRoute } = routeParams ?? {};
  const mappedPage = mapPageValue(page ?? routePage);
  const pathname = usePathname();
  const router = useRouter();
  const categories = useSportsbookRoutes();
  const signIn = useSignIn();
  const { openDepositSheet } = useGlobalSheet();
  const { invalidateQuery, invalidateQueriesStartingWith } = useApiInvalidate();
  const isSDKLoaded = useAltenarSDKLoaded();

  const handleSignInButtonClick = useCallback(() => {
    signIn({ callbackUrl: pathname ?? ROUTES.SPORTS.DISCOVERY });
  }, [pathname, signIn]);

  const handleInsufficientBalance = useCallback(() => {
    openDepositSheet({
      onSuccessCallback: () => window.location.reload(),
    });
  }, [openDepositSheet]);

  const handleBetPlacement = useCallback(() => {
    invalidateQuery(API_ROUTES.MY_ACCOUNT.WALLET.GET_BALANCE);
  }, [invalidateQuery]);

  const handleRouteChange = useCallback(
    (routeParams: RouteParams = {}) => {
      invalidateQueriesStartingWith(API_ROUTES.SPORTSBOOK.FREE_BET.split(':')[0]!);

      if (Object.keys(routeParams).length === 0) return;

      routeChangeCountRef.current += 1;

      const urlParams = buildUrlParamsFromRouteParams(routeParams);
      const target = resolveSportsbookPathname(routeParams, categories);

      if (target === null) {
        updateUrlParams(urlParams);
        return;
      }

      const isHubTarget = target === ROUTES.SPORTS.ROOT_SPORTSBOOK_PATHNAME;

      if (target !== window.location.pathname) {
        router.push(isHubTarget ? buildSportsbookHref(target, urlParams) : target);
        return;
      }

      updateUrlParams(isHubTarget ? urlParams : {});
    },
    [invalidateQueriesStartingWith, categories, router]
  );

  const teardownAltenarPopupCentring = useCallback(() => {
    popupCentringContainerObserverRef.current?.disconnect();
    popupCentringContainerObserverRef.current = null;
    popupCentringShadowObserverRef.current?.disconnect();
    popupCentringShadowObserverRef.current = null;
    watchedShadowRootRef.current = null;
  }, []);

  const setupAltenarPopupCentring = useCallback(() => {
    teardownAltenarPopupCentring();

    const container = sportsBookRef.current;
    if (!container) {
      return;
    }

    const applyCentring = () => {
      centreAltenarPopups(container);

      const shadowRoot = watchedShadowRootRef.current;
      if (!shadowRoot) {
        return;
      }

      centreAltenarPopups(shadowRoot);
    };

    const attachShadowRootObserver = (shadowRoot: ShadowRoot) => {
      if (watchedShadowRootRef.current === shadowRoot) {
        applyCentring();
        return;
      }

      popupCentringShadowObserverRef.current?.disconnect();
      watchedShadowRootRef.current = shadowRoot;

      applyCentring();

      const shadowObserver = new MutationObserver(applyCentring);
      shadowObserver.observe(shadowRoot, { childList: true, subtree: true });
      popupCentringShadowObserverRef.current = shadowObserver;
    };

    const syncFromContainer = () => {
      const shadowRoot = container.firstElementChild?.shadowRoot ?? null;
      if (shadowRoot) {
        attachShadowRootObserver(shadowRoot);
      }
    };

    syncFromContainer();

    const containerObserver = new MutationObserver(() => {
      syncFromContainer();
      centreAltenarPopups(container);
    });
    containerObserver.observe(container, { childList: true, subtree: true });
    popupCentringContainerObserverRef.current = containerObserver;
  }, [teardownAltenarPopupCentring]);

  // Altenar's back arrow only reports `onGoBack` (no route). A widget that has not navigated since it was mounted has no
  // internal history, so it falls back to its overview, i.e. the main sportsbook page.
  const handleGoBack = useCallback(() => {
    if (routeChangeCountRef.current > 0) return;

    router.push(ROUTES.SPORTS.ROOT_SPORTSBOOK_PATHNAME);
  }, [router]);

  const initializeSportsbook = useCallback(async () => {
    const browserWindow = window as WindowWithSportsbookSDK;

    if (!sportsBookRef.current || !browserWindow.altenarWSDK) {
      return;
    }

    try {
      if (browserWindow.omni?.SB) {
        browserWindow.omni.SB.remove();
      }

      browserWindow.omni = browserWindow.omni || {};

      const sportsBookParams: SportsBookParams = withRacingType({
        ...initialRoute,
        ...(initialRoute.shareCode ? { defaultStake: SHARE_CODE_DEFAULT_STAKE } : {}),
        page: mappedPage,
        onSignInButtonClick: handleSignInButtonClick,
        onInsufficientBalance: quickDepositEnabled ? handleInsufficientBalance : undefined,
        onBetPlacement: handleBetPlacement,
        onRouteChange: handleRouteChange,
        onGoBack: handleGoBack,
        routerType: 'memory',
        theme: {
          primaryColor: 'hsl(var(--accent-green))',
          accentColor: 'hsl(var(--accent-green-foreground))',
        },
        showHeader: false,
        showBanner: false,
      });

      browserWindow.omni.SB = await browserWindow.altenarWSDK.addSportsBook({
        props: sportsBookParams,
        container: sportsBookRef.current,
      });

      setupAltenarPopupCentring();

      setIsWidgetInitialized(true);

      handleRouteChange();

      updatePageCssClasses();
    } catch (error) {
      debug.error('Failed to initialize Sportsbook widget:', error);
    }
  }, [
    mappedPage,
    initialRoute,
    quickDepositEnabled,
    handleSignInButtonClick,
    handleInsufficientBalance,
    handleBetPlacement,
    handleRouteChange,
    handleGoBack,
    debug,
    setupAltenarPopupCentring,
  ]);

  useEffect(() => {
    if (!isSDKLoaded || isWidgetInitialized || !sportsBookRef.current) {
      return;
    }

    initializeSportsbook();
  }, [isSDKLoaded, isWidgetInitialized, initializeSportsbook]);

  useEffect(() => {
    return () => {
      const contentElement = document.querySelector('#content');
      const pageElement = document.querySelector('#page');

      if (contentElement) {
        contentElement.classList.remove('sportsbook-page');
      }
      if (pageElement) {
        pageElement.classList.remove('sportsbook-page');
      }

      teardownAltenarPopupCentring();

      const browserWindow = window as WindowWithSportsbookSDK;
      if (browserWindow.omni?.SB) {
        try {
          browserWindow.omni.SB.remove();
        } catch (error) {
          debug.error('Error removing SB widget during cleanup:', error);
        }
      }
    };
  }, [debug, teardownAltenarPopupCentring]);

  return (
    <div className={`w-full min-h-screen bg-background ${className}`} data-testid="sportsbook-widget">
      <div ref={sportsBookRef} className={SPORTSBOOK_CONTAINER_CLASS} />
    </div>
  );
}

export { SportsbookWidget };
